import React, { ReactElement, useState } from 'react';
import useSWR from 'swr';
import Head from 'next/head';
import { GetStaticPropsResult } from 'next';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { MEDIA_QUERY } from '@bucketplace/design-system';

import { PromotionResponse } from 'promotions/types';
import { PromotionsTemplate } from 'promotions/components/PromotionsTemplate';
import { Error } from 'components/Error';
import { API_GATEWAY_HOST } from 'common';
import { serverHttp } from 'common/http';

import { usePromotionQuery } from 'promotions/hooks/usePromotionQuery';

const isProd = process.env.APP_PROFILE === 'prod';
const PROMOTION_ID = isProd ? 15188 : 12431;
const EVENT_API_PATH = '/api/promotions';
const IMAGE_URL = 'https://prs.ohousecdn.com/apne2/commerce/big-promotion/v1-378939793854528.png';

const FestaSale = ({
  data: promotionData,
}: {
  data: PromotionResponse,
}): ReactElement => {
  const [isEditorLoaded, setIsEditorLoaded] = useState(false);
  const router = useRouter();

  const {
    promotionId,
    anchorSection,
    anchorTargetId,
  } = usePromotionQuery({ isProd, defaultId: PROMOTION_ID });

  const { data: csrPromotionData, error: promotionError } = useSWR<PromotionResponse>(
    `${EVENT_API_PATH}/${promotionId}`,
    {
      isPaused: () => !!promotionData,
    }
  );

  promotionData = promotionData ?? csrPromotionData;
  const isLoading = !promotionData && !promotionError || !isEditorLoaded;
  const isInitialAnchor = anchorSection === 'true';

  if (promotionData?.['statusCode'] >= 400) {
    return <Error statusCode={promotionData['statusCode']} />;
  }


  if (!router.isReady || !promotionData?.exhibition) {
    return null;
  }
  const promotion = promotionData.exhibition;
  const { units, id: detailId } = promotion.details[0];

  return (
    <Template
      units={units}
      promotionId={promotion.id}
      title={promotion.title}
      coverImageUrls={promotion.coverImageUrls}
      detailId={detailId}
      anchorInfo={{
        targetId: anchorTargetId,
        isInitialAnchor,
      }}
      endDate={promotionData.exhibition.endAt}
      isLoading={isLoading}
      onViewerLoaded={(isLoaded: boolean) => {
        setIsEditorLoaded(isLoaded);
      }}
    />
  );
};

export const getStaticProps = async (): Promise<GetStaticPropsResult<any>> => {
  const isBuildPhase = process.env.NEXT_PHASE === 'phase-production-build';
  if (isBuildPhase) {
    return {
      props: {
        data: null,
      },
      revalidate: 1,
    };
  }

  const json = await serverHttp.get(`${API_GATEWAY_HOST}/exhibition/gateway/exhibition/user/v1/get?id=${PROMOTION_ID}`).json();

  return {
    props: {
      data: json,
    },
    revalidate: 1,
  };
};

export default FestaSale;

FestaSale.getLayout = function getLayout(page) {
  const title = '집요한세일';
  const description = '집요한 만큼. 탁월한 세일.';

  return (
    <>
      <Head>
        <title key="head-title">{ title }</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content="https://events.ohou.se/promotions/big-promotion" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={IMAGE_URL} />
      </Head>
      { page }
    </>
  );
};

const Template = styled(PromotionsTemplate)`
  padding: 0 60px;
  ${MEDIA_QUERY.max('tablet', `
    padding: 0;
  `)}
`;
